import type { PipelineRunResponse, TemplateInfo, TemplateConfig, RunPreset } from '@/types/pipeline';
import { getAuthHeaders, apiFetch } from '@/utils/auth';

const BE_URL = (import.meta.env.VITE_QS_BE_URL as string) ?? '';

// Guard against SSO/auth-gateway redirect responses that return HTML instead of
// JSON (the API gateway intercepts 401s and redirects to the login portal).
// fetch() follows redirects automatically so the final response has status 200
// but a text/html body — res.json() would throw a SyntaxError without this check.
function safeJson<T = unknown>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    return Promise.reject(
      new Error(`Expected JSON but received non-JSON response (${res.status}) — possible auth redirect`),
    );
  }
  return res.json() as Promise<T>;
}

export interface DataSource {
  id: string;
  display_name: string;
  source_type: 'inline' | 'local_file' | 'remote';
  file_path: string | null;
  remote_location: string | null;
  connection_type: string | null;
  data_format: string | null;
  original_filename: string | null;
  size_bytes: number | null;
  row_count: number | null;
  column_count: number | null;
  column_names: string[] | null;
  status: string;
  created_at: string;
}

export async function listDataSources(): Promise<DataSource[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/data-sources', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Data sources fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function uploadDataSource(file: File, displayName: string): Promise<DataSource> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('display_name', displayName);
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/data-sources/upload', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Upload failed');
  }
  return safeJson(res);
}

export async function uploadFileToVolume(
  file: File,
  displayName: string,
  workspaceId: string = '',
): Promise<DataSource> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('display_name', displayName);
  formData.append('workspace_id', workspaceId);
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/volumes/upload-file', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail?.message || err.detail || 'Volume upload failed');
  }
  return safeJson(res);
}

export async function createRemoteDataSource(
  displayName: string,
  location: string,
  connectionType: string,
  format: string,
): Promise<DataSource> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/data-sources/remote', {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      display_name: displayName,
      remote_location: location,
      connection_type: connectionType,
      data_format: format,
    }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.detail || 'Failed to create remote reference');
  }
  return safeJson(res);
}

export async function updateDataSource(
  id: string,
  updates: { display_name?: string; remote_location?: string; connection_type?: string },
): Promise<DataSource> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/data-sources/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Update failed: ${res.status}`);
  return safeJson(res);
}

export async function deleteDataSource(id: string): Promise<void> {
  await apiFetch(`${BE_URL}/api/v1/pipeline/data-sources/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
}

export async function downloadDataSource(id: string, filename: string): Promise<void> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/data-sources/${id}/download`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function listTemplates(): Promise<TemplateInfo[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/templates', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Templates fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function launchPipeline(
  message: string,
  inputData?: Record<string, unknown>,
  templateName?: string,
  configOverrides?: Record<string, Record<string, unknown>>,
  presetId?: string,
  presetName?: string,
  displayName?: string,
): Promise<PipelineRunResponse> {
  const body: Record<string, unknown> = {
    message,
    input_data: inputData ?? null,
    template_name: templateName ?? null,
  };
  if (configOverrides != null) body.config_overrides = configOverrides;
  if (presetId != null) body.preset_id = presetId;
  if (presetName != null) body.preset_name = presetName;
  if (displayName != null && displayName.trim() !== '') body.display_name = displayName.trim();
  const res = await apiFetch(BE_URL + '/api/v1/pipeline', {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Launch failed: ${res.status}`);
  return safeJson(res);
}

export async function patchPipelineRun(
  runId: string,
  displayName: string,
): Promise<PipelineRunResponse> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}`, {
    method: 'PATCH',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ display_name: displayName.trim() || null }),
  });
  if (!res.ok) throw new Error(`Rename failed: ${res.status}`);
  return safeJson(res);
}

export async function fetchTemplateConfig(templateName: string): Promise<TemplateConfig> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/templates/${encodeURIComponent(templateName)}/config`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Template config fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function fetchPresets(templateName?: string): Promise<RunPreset[]> {
  const url = templateName
    ? `${BE_URL}/api/v1/pipeline/presets?template_name=${encodeURIComponent(templateName)}`
    : BE_URL + '/api/v1/pipeline/presets';
  const res = await apiFetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Presets fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function createPreset(payload: {
  template_name: string;
  name: string;
  config_overrides: Record<string, Record<string, unknown>>;
}): Promise<RunPreset> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/presets', {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Create preset failed: ${res.status}`);
  return safeJson(res);
}

export async function updatePreset(
  id: string,
  payload: { name?: string; config_overrides?: Record<string, Record<string, unknown>> },
): Promise<RunPreset> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/presets/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Update preset failed: ${res.status}`);
  return safeJson(res);
}

export async function deletePreset(id: string): Promise<void> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/presets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Delete preset failed: ${res.status}`);
}

// ── Databricks ad-hoc run — generic (all templates) ──────────────────────────

export async function launchDatabricksRun(
  templateName: string,
  inputFilePath: string,
  workspaceId: string = '',
  connectionType: string = '',
  inputKeyPath: string = '',
): Promise<{ run_id: string; output_path: string }> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/databricks-run', {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      template_name: templateName,
      input_file_path: inputFilePath,
      workspace_id: workspaceId,
      connection_type: connectionType,
      input_key_path: inputKeyPath,
    }),
  });
  if (!res.ok) throw new Error(`Databricks launch failed: ${res.status}`);
  return safeJson(res);
}

export async function getDatabricksRunStatus(
  runId: string,
  workspaceId: string = '',
): Promise<{ run_id: string; life_cycle_state: string; result_state: string; done: boolean; success: boolean; state_message: string }> {
  const params = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/databricks-run/${encodeURIComponent(runId)}/status${params}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function getDatabricksRunOutput(
  runId: string,
  outputPath: string,
  workspaceId: string = '',
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams({ output_path: outputPath });
  if (workspaceId) params.set('workspace_id', workspaceId);
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/databricks-run/${encodeURIComponent(runId)}/output?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Output fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function generateDatabricksReport(
  runId: string,
  output: Record<string, unknown>,
  templateName: string = 'activity_analysis',
): Promise<{ report_markdown: string; section_count: number; chart_count: number; summary: string }> {
  // Send only the fields the backend actually reads — not the full output.json blob.
  // chart_data (heatmap pivot) can be 10k+ records; metadata_profile.columns is per-column.
  // Backend only needs chart IDs, summaries, and aggregated profile scalars.
  const chartsRaw = output.charts as Record<string, unknown> | undefined;
  const chartIds: Record<string, Record<string, unknown>> = chartsRaw
    ? Object.fromEntries(Object.keys(chartsRaw).map(id => [id, {}]))
    : {};

  const metaRaw  = output.metadata_profile     as Record<string, unknown> | undefined;
  const capRaw   = output.capability_profile    as Record<string, unknown> | undefined;
  const prepRaw  = output.prepare_data_profile  as Record<string, unknown> | undefined;
  const segRaw   = output.segmentation_profile  as Record<string, unknown> | undefined;
  const benchRaw = output.benchmarking_profile  as Record<string, unknown> | undefined;
  const basketRaw = output.basket_profile       as Record<string, unknown> | undefined;
  const metricsRaw = output.metrics             as Record<string, unknown> | undefined;

  const slimOutput = {
    row_count: output.row_count ?? 0,
    step_summaries: output.step_summaries ?? {},
    charts: chartIds,
    metadata_profile: {
      row_count: metaRaw?.row_count ?? 0,
      column_count: metaRaw?.column_count ?? 0,
      quality_score: metaRaw?.quality_score ?? 1,
    },
    capability_profile: {
      l2_count: capRaw?.l2_count ?? 0,
      l1_count: capRaw?.l1_count ?? 0,
      total_long_rows: capRaw?.total_long_rows ?? 0,
      top_l2_capabilities: (capRaw?.top_l2_capabilities as unknown[] | undefined)?.slice(0, 20) ?? [],
      l1_distribution: capRaw?.l1_distribution ?? [],
    },
    prepare_data_profile: {
      columns_retained: prepRaw?.columns_retained ?? 0,
      rows_processed: prepRaw?.rows_processed ?? 0,
      transformations: prepRaw?.transformations ?? [],
    },
    // Customer Segmentation
    ...(segRaw ? { segmentation_profile: segRaw } : {}),
    // M&A Benchmarking
    ...(benchRaw ? { benchmarking_profile: benchRaw } : {}),
    // Market Basket
    ...(basketRaw ? { basket_profile: basketRaw } : {}),
    // Generic metrics (n_functions, naics_code, etc.)
    ...(metricsRaw ? { metrics: metricsRaw } : {}),
  };

  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/databricks-run/${encodeURIComponent(runId)}/generate-report`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ output: slimOutput, template_name: templateName }),
  });
  if (!res.ok) throw new Error(`Report generation failed: ${res.status}`);
  return safeJson(res);
}

/** @deprecated Use launchDatabricksRun instead */
export async function launchActivityAnalysisDatabricks(
  inputFilePath: string,
  workspaceId: string = '',
): Promise<{ run_id: string; output_path: string }> {
  return launchDatabricksRun('activity_analysis', inputFilePath, workspaceId);
}

export async function listPipelineRuns(): Promise<PipelineRunResponse[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/runs', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`List failed: ${res.status}`);
  return safeJson(res);
}

export async function getPipelineRun(runId: string): Promise<PipelineRunResponse> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Poll failed: ${res.status}`);
  return safeJson(res);
}

export async function getPipelineReport(runId: string): Promise<Record<string, unknown>> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/report`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Report fetch failed:${res.status}`);
  return safeJson(res);
}

export async function getToolOutput(
  runId: string,
  stepType: string,
  toolName: string,
): Promise<Record<string, unknown>> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/step/${stepType}/tool/${toolName}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Tool output not found');
  return safeJson(res);
}

export interface CheckpointInfo {
  is_paused: boolean;
  step_type?: string;
  step_order?: number;
  checkpoint?: {
    gate: string;
    prompt: string;
    context?: Record<string, unknown>;
    output_preview?: {
      summary?: string;
      findings?: Array<Record<string, unknown>>;
    };
  };
}

export async function getCheckpoint(runId: string): Promise<CheckpointInfo> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/checkpoint`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Checkpoint fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function resumePipeline(runId: string): Promise<PipelineRunResponse> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/resume`, { method: 'POST', headers: getAuthHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail?.message || err.message || `Resume failed: ${res.status}`);
  }
  return safeJson(res);
}

// ── Databricks Use Cases ──────────────────────────────────────────────────────

export interface UseCaseNotice {
  key: string;
  text: string;
}

export interface UseCaseThirdPartyDataset {
  name: string;
}

export interface UseCase {
  id: string;
  name: string;
  domain: string;
  description: string;
  first_party_input: string;
  requires_first_party: boolean;
  third_party_datasets: UseCaseThirdPartyDataset[];
  notices: UseCaseNotice[];
  databricks_job_id: string;
  input_volume_path?: string;
}

export async function listUseCases(): Promise<UseCase[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/use-cases', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Use cases fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function runTemplateDatabricks(
  templateName: string,
  params: {
    raw_file_path: string;
    key_file_path?: string;
    output_folder_path?: string;
    cluster_id?: string;
  },
): Promise<{ databricks_run_id: string; template_name: string; output_folder_path: string; script_volume_path: string; submitted_at: string }> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/templates/${encodeURIComponent(templateName)}/databricks-run`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail?.message || `Databricks run failed: ${res.status}`);
  }
  return safeJson(res);
}

export interface DatabricksRunState {
  life_cycle_state: 'PENDING' | 'RUNNING' | 'TERMINATING' | 'TERMINATED' | 'SKIPPED' | 'INTERNAL_ERROR';
  result_state?: 'SUCCESS' | 'FAILED' | 'TIMEDOUT' | 'CANCELED' | 'MAXIMUM_CONCURRENT_RUNS_REACHED';
  state_message?: string;
}

export interface DatabricksRunTask {
  task_key: string;
  state: DatabricksRunState;
  start_time?: number;
  end_time?: number;
}

export interface DatabricksRunStatus {
  run_id: number;
  run_name: string;
  state: DatabricksRunState;
  tasks?: DatabricksRunTask[];
  start_time?: number;
  end_time?: number;
}

export async function triggerUseCaseJob(
  useCaseId: string,
  params: {
    input_file_path: string;
    project_description: string;
    key_file_path?: string;
  },
): Promise<{ databricks_run_id: string; use_case_id: string; output_folder_path: string; submitted_at: string }> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/use-cases/${encodeURIComponent(useCaseId)}/run`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail?.message || `Job trigger failed: ${res.status}`);
  }
  return safeJson(res);
}

export async function getUseCaseJobStatus(
  useCaseId: string,
  runId: string,
): Promise<{ run_id: string; life_cycle_state: string; result_state: string | null; state_message: string }> {
  const res = await apiFetch(
    `${BE_URL}/api/v1/pipeline/use-cases/${encodeURIComponent(useCaseId)}/run/${encodeURIComponent(runId)}/status`,
    { headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
  return safeJson(res);
}

// ── Unity Catalog browsing ────────────────────────────────────────────────────

export interface UCCatalog {
  name: string;
  comment?: string;
}

export interface UCSchema {
  name: string;
  catalog_name: string;
  comment?: string;
}

export interface UCTable {
  name: string;
  catalog_name: string;
  schema_name: string;
  table_type?: string;
  comment?: string;
}

export async function listUCCatalogs(): Promise<UCCatalog[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/unity-catalog/catalogs', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Catalog fetch failed: ${res.status}`);
  const data = await safeJson(res);
  if (data.manual_entry) throw new Error('Unity Catalog access not available with current credentials');
  return data.catalogs ?? [];
}

export async function listUCSchemas(catalogName: string): Promise<UCSchema[]> {
  const res = await apiFetch(
    `${BE_URL}/api/v1/pipeline/unity-catalog/schemas?catalog_name=${encodeURIComponent(catalogName)}`,
    { headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Schema fetch failed: ${res.status}`);
  const data = await safeJson(res);
  return data.schemas ?? [];
}

export async function listUCTables(catalogName: string, schemaName: string): Promise<UCTable[]> {
  const res = await apiFetch(
    `${BE_URL}/api/v1/pipeline/unity-catalog/tables?catalog_name=${encodeURIComponent(catalogName)}&schema_name=${encodeURIComponent(schemaName)}`,
    { headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Tables fetch failed: ${res.status}`);
  const data = await safeJson(res);
  return data.tables ?? [];
}

export interface VolumeEntry {
  name: string;
  path: string;
  is_dir: boolean;
  size_bytes: number | null;
}

export interface DatabricksWorkspace {
  id: string;
  name: string;
}

export async function listWorkspaces(): Promise<DatabricksWorkspace[]> {
  const res = await apiFetch(BE_URL + '/api/v1/pipeline/workspaces', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Workspaces fetch failed: ${res.status}`);
  const data = await safeJson(res);
  return data.workspaces ?? [];
}

export async function browseVolume(path: string, workspaceId?: string): Promise<VolumeEntry[]> {
  const params = new URLSearchParams({ path });
  if (workspaceId) params.set('workspace_id', workspaceId);
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/volumes/browse?${params.toString()}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Volume browse failed: ${res.status}`);
  const data = await safeJson(res);
  return data.files ?? [];
}

export async function getDatabricksConfig(): Promise<{ catalog: string; schema: string; input_volume: string }> {
  const res = await apiFetch(BE_URL + '/api/v1/config/databricks', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function browseAllVolumeFiles(workspaceId: string): Promise<VolumeEntry[]> {
  const { catalog, schema } = await getDatabricksConfig();
  const scopedPath = `/Volumes/${catalog}/${schema}`;
  const params = new URLSearchParams({ path: scopedPath, recursive: 'true', workspace_id: workspaceId });
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/volumes/browse?${params.toString()}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Volume browse failed: ${res.status}`);
  const data = await safeJson(res);
  return data.files ?? [];
}

export interface DashboardData {
  run_id: string;
  use_case_id: string;
  status: 'ok' | 'no_output';
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  output_file?: string;
}

export async function fetchDashboardData(runId: string): Promise<DashboardData> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/dashboard-data`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Dashboard data fetch failed: ${res.status}`);
  return safeJson(res);
}

export interface DebugStepEntry {
  id: string;
  step_type: string;
  step_order: number;
  display_name: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  input_context: Record<string, unknown> | null;
  output_payload: Record<string, unknown> | null;
  checkpoint_data: unknown;
}

export interface DebugRunResponse {
  id: string;
  template_name: string;
  status: string;
  created_at: string | null;
  completed_at: string | null;
  input_data_summary: unknown;
  steps: DebugStepEntry[];
}

export async function getPipelineRunDebug(runId: string): Promise<DebugRunResponse> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/debug`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Debug fetch failed: ${res.status}`);
  return safeJson(res);
}

export async function retriggerStep(
  runId: string,
  stepType: string,
  retriggerDownstream: boolean,
): Promise<void> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/step/${encodeURIComponent(stepType)}/retrigger`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ retrigger_downstream: retriggerDownstream }),
  });
  if (!res.ok) throw new Error(`Retrigger failed: ${res.status}`);
}

export async function sendCommand(
  runId: string,
  commandType: string,
  payload?: Record<string, unknown>,
): Promise<{ ok: boolean; error?: string; action?: string }> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/command`, {
    method: 'POST',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ command_type: commandType, payload }),
  });
  if (res.status === 409) {
    const err = await res.json().catch(() => ({}));
    if (err.error === 'command_pending') {
      // A previous submission is already queued — treat as success.
      return { ok: true };
    }
    return {
      ok: false,
      error: 'This review step is no longer active. The pipeline may have already continued — check the status above.',
      action: 'resume',
    };
  }
  if (!res.ok) {
    try {
      const err = await res.json();
      return { ok: false, error: err.message || err.detail || `HTTP ${res.status}` };
    } catch {
      return { ok: false, error: `HTTP ${res.status}` };
    }
  }
  return { ok: true };
}

export async function publishLakeviewDashboard(
  runId: string,
): Promise<{ dashboard_id: string; dashboard_url: string }> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/lakeview-dashboard`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return safeJson(res);
}

export interface WholesaleDashboardRow {
  company: string;
  year: number;
  revenue: number | null;
  gross_profit: number | null;
  gross_margin_pct: number | null;
  sga_expenses: number | null;
  sga_margin_pct: number | null;
  ebit: number | null;
  ebit_margin_pct: number | null;
  ebitda: number | null;
  ebitda_margin_pct: number | null;
  net_income: number | null;
  inventory_turnover: number | null;
  days_on_hand: number | null;
  cogs: number | null;
  fcf: number | null;
  fcf_margin_pct: number | null;
  total_assets: number | null;
  total_debt: number | null;
  total_equity: number | null;
  roa: number | null;
  roc: number | null;
}

export interface WholesaleDashboardData {
  companies: string[];
  years: number[];
  rows: WholesaleDashboardRow[];
}

export async function fetchWholesaleDashboardCharts(runId: string): Promise<WholesaleDashboardData> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/dashboard-charts`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return safeJson(res);
}

/** Full row — column names match dashboard_template.json field aliases exactly */
export interface WholesaleRow {
  company: string;
  year: number;
  industry: string | null;
  // income statement
  revenue: number | null;
  cogs: number | null;
  gross_profit: number | null;
  gross_margin_pct: number | null;
  sga_expenses: number | null;
  sga_margin: number | null;
  da: number | null;
  ebit: number | null;
  ebit_margin: number | null;
  ebitda: number | null;
  ebitda_margin: number | null;
  net_income: number | null;
  // balance sheet
  cash: number | null;
  ar: number | null;
  inventory: number | null;
  total_current_assets: number | null;
  gross_ppe: number | null;
  accumulated_depreciation: number | null;
  goodwill: number | null;
  total_intangible_assets: number | null;
  total_assets: number | null;
  ap: number | null;
  total_current_liabilities: number | null;
  total_debt: number | null;
  total_equity: number | null;
  net_working_capital: number | null;
  // cash flows
  capex: number | null;
  fcf: number | null;
  fcf_margin: number | null;
  // cross statements
  inventory_turnover: number | null;
  days_on_hand: number | null;
  fixed_asset_turnover: number | null;
  roa: number | null;
  cagr_revenue_5y: number | null;
  cagr_ebitda_5y: number | null;
  // scatter / returns
  roc: number | null;
  revenue_growth_pct: number | null;
}

/** Pre-computed per-year industry averages from the backend (mirrors Databricks industry_avg SQL) */
export interface WholesaleIndustryAvgRow {
  year: number;
  avg_gross_margin_pct: number | null;
  avg_sga_margin: number | null;
  avg_ebit_margin: number | null;
  avg_ebitda_margin: number | null;
  avg_revenue_growth_pct: number | null;
}

export interface WholesaleChartsResponse {
  companies: string[];
  years: number[];
  rows: WholesaleRow[];
  /** Server-computed industry averages — use for line charts instead of JS aggregation */
  industry_avg?: WholesaleIndustryAvgRow[];
}

export async function fetchWholesaleChartImages(runId: string): Promise<WholesaleChartsResponse> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/chart-images`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return safeJson(res);
}

export interface WarehouseStatus {
  warehouse_id: string;
  name: string;
  state: string;
  available: boolean;
}

export async function fetchWarehouseStatus(runId: string, warehouseId?: string): Promise<WarehouseStatus> {
  const query = warehouseId ? `?warehouse_id=${encodeURIComponent(warehouseId)}` : '';
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/warehouse-status${query}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return safeJson(res);
}

export async function startWarehouse(runId: string): Promise<{ warehouse_id: string; state: string; message: string }> {
  const res = await apiFetch(`${BE_URL}/api/v1/pipeline/${runId}/warehouse-start`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return safeJson(res);
}
